import { useState } from "react";
import { Check, Tag, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";

interface Props {
  label: string;
  suggestions: string[];
  onPick: (label: string) => void;
  hint?: string;
}

// Combobox for promo-day labels: pick an existing label or create one by
// typing. A picked label renders as a removable chip in place of the trigger.
export default function LabelPicker({ label, suggestions, onPick, hint }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = suggestions.filter((s) => s.toLowerCase().includes(q));
  const canCreate = q.length > 0 && !suggestions.some((s) => s.toLowerCase() === q);

  const pick = (value: string) => {
    onPick(value);
    setOpen(false);
    setQuery("");
  };

  if (label) {
    return (
      <span
        style={{ background: "var(--background)", borderColor: "var(--border)", color: "var(--foreground)" }}
        className="inline-flex items-center gap-1.5 border rounded-full text-xs pl-2.5 pr-1 py-1"
      >
        <Tag className="h-3 w-3" style={{ color: "var(--muted-foreground)" }} />
        {label}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPick("")}
          className="h-4 w-4"
          aria-label="Remove label"
        >
          <X className="h-3 w-3" />
        </Button>
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
          <Tag className="h-3.5 w-3.5" />
          Add label
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1.5">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (filtered[0] || canCreate)) pick(canCreate ? query.trim() : filtered[0]);
          }}
          placeholder="Search or create…"
          style={{ background: "var(--background)", borderColor: "var(--border)", color: "var(--foreground)" }}
          className="w-full border text-xs rounded-md px-2 py-1.5 mb-1 focus:outline-none focus:border-primary"
        />
        <div className="max-h-48 overflow-y-auto">
          {filtered.map((s) => (
            <Button
              key={s}
              variant="ghost"
              size="sm"
              onClick={() => pick(s)}
              className="w-full justify-start gap-2 h-7 px-2 text-xs font-normal"
            >
              <Tag className="h-3 w-3 shrink-0" style={{ color: "var(--muted-foreground)" }} />
              {s}
            </Button>
          ))}
          {canCreate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => pick(query.trim())}
              className="w-full justify-start gap-2 h-7 px-2 text-xs font-normal"
            >
              <Check className="h-3 w-3 shrink-0" style={{ color: "var(--muted-foreground)" }} />
              Create “{query.trim()}”
            </Button>
          )}
          {filtered.length === 0 && !canCreate && (
            <div style={{ color: "var(--muted-foreground)" }} className="px-2 py-1.5 text-xs">
              Type to create a label
            </div>
          )}
        </div>
        {hint && (
          <div
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="border-t mt-1 px-2 pt-1.5 pb-0.5 text-[10px] leading-snug"
          >
            {hint}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
